import React from 'react';
import { Select } from '@mantine/core';

export interface AfaSelectProps {
  /** Datos para el select - array de strings o objetos con value/label */
  data: string[] | { value: string; label: string }[];
  
  /** Valor seleccionado actual */
  value?: string;
  
  /** Callback cuando cambia la selección */
  onChange?: (value: string | null) => void;
  
  /** Dato que viene de Editar */
  defaultValue?: string;

  /** Placeholder del input */
  placeholder?: string;
  
  /** Label del campo */
  label?: string;
  
  /** Descripción adicional */
  description?: string;
  
  /** Mensaje de error */
  error?: string;
  
  /** Si es requerido */
  required?: boolean;
  
  /** Si está deshabilitado */
  disabled?: boolean;
  
  /** Tamaño del input */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Filtrable por texto */
  searchable?: boolean;
  
  /** Limpiar selección habilitado */
  clearable?: boolean;
  
  /** Número máximo de opciones a mostrar */
  limit?: number;
  
  /** Texto cuando no hay resultados */
  nothingFoundMessage?: string;
  
  /** Nombre del campo para formularios */
  name?: string;
  
  /** ID del campo */
  id?: string;
  
  /** Props adicionales para el Select */
  selectProps?: any;
  
  /** Estilos personalizados */
  styles?: any;
  
  /** Clase CSS */
  className?: string;
}

/**
 * Wrapper del Select de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico con array de strings
 * <AfaSelect
 *   data={['Pequeño', 'Mediano', 'Grande']}
 *   placeholder="Selecciona tamaño"
 *   label="Tamaño"
 * />
 * 
 * // Con objetos value/label
 * <AfaSelect
 *   data={[
 *     { value: 'es', label: 'Español' },
 *     { value: 'ca', label: 'Català' }
 *   ]}
 *   searchable
 *   clearable
 * />
 * 
 * // Controlado
 * const [value, setValue] = useState('');
 * <AfaSelect
 *   data={options}
 *   value={value}
 *   onChange={setValue}
 * />
 */
export function AfaSelect({
  data,
  value,
  onChange,
  defaultValue,
  placeholder = 'Seleccioni opció',
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = 'sm',
  radius = 'md',
  searchable = false,
  clearable = false,
  // limit = 10000,
  nothingFoundMessage = 'No s\'han trobat resultats',
  name,
  id,
  selectProps,
  styles,
  className,
}: AfaSelectProps) {

  return (
    <Select
      data={data}
      value={onChange !== undefined ? value : undefined}
      defaultValue={defaultValue}
      onChange={onChange}
      //defaultValue={defaultValue} 
      placeholder={placeholder}
      label={label}
      description={description}
      error={error}
      name={name}
      id={id}
      required={required}
      disabled={disabled}
      size={size}
      radius={radius}
      searchable={searchable}
      clearable={clearable}
      // limit={limit}
      nothingFoundMessage={nothingFoundMessage}
      styles={{
        input: {
          borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
            boxShadow: `0 0 0 2px var(--mantine-color-brand-1)`,
          },
        },
        label: {
          color: 'var(--mantine-other-black)',
          fontWeight: 500,
        },
        dropdown: {
          borderColor: 'var(--mantine-color-brand-3)',
        },
        option: {
          '&[data-selected]': {
            backgroundColor: 'var(--mantine-color-brand-0)',
            color: 'var(--mantine-color-brand-8)',
          },
          '&:hover': {
            backgroundColor: 'var(--mantine-color-brand-1)',
          },
        },
        ...styles,
      }}
      {...(className && { className })}
      {...selectProps}
    />
  );
}